import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Column, Row, Typography } from 'seagull';
import { Bulb } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { isFutureEffectiveDate } from '~/business-logic/billingLogic';
import { useResponsive } from '~/client-flow/contexts';
import { buildSystemVariableResolution } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { useIoContext, useStore } from '~/contexts';
import { relationshipModel } from '~/models';
import { toDate } from '~/models/time/translator';
import { selectors } from '~/store/state-selectors';
import { useCobConfig } from '../../../../CobConfigContext';
import { usePackageSelection } from '../../../hooks/usePackageSelection';
import { PackageCard } from './PackageCard';
import styles from './SelectAPackage.module.scss';
export const GetSelectAPackageTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const SelectAPackage = (param)=>{
    let { testId } = param;
    var _relationship_serviceBundles;
    const { SelectAPackageTestIds } = useMemo(()=>GetSelectAPackageTID(testId), [
        testId
    ]);
    const { relationship, isPreview } = useCobConfig();
    const { isMobile } = useResponsive();
    const { getNow } = useIoContext();
    const userId = useStore(selectors.user.id);
    const isBuyer = relationshipModel.isBuyer(relationship, userId);
    const hasSignatories = relationshipModel.hasSignatories(relationship);
    const hasAdditionalRecipients = hasSignatories || relationship.reviewers.length > 0;
    const packages = useMemo(()=>{
        var _ref;
        var _relationship_serviceBundles;
        return (_ref = (_relationship_serviceBundles = relationship.serviceBundles) === null || _relationship_serviceBundles === void 0 ? void 0 : _relationship_serviceBundles.bundles) !== null && _ref !== void 0 ? _ref : [];
    }, [
        (_relationship_serviceBundles = relationship.serviceBundles) === null || _relationship_serviceBundles === void 0 ? void 0 : _relationship_serviceBundles.bundles
    ]);
    const isEffectiveDateInFuture = isFutureEffectiveDate(relationship === null || relationship === void 0 ? void 0 : relationship.effectiveDate, getNow());
    const buyerFullName = relationshipModel.buyerFullName(relationship);
    const { selectedBundleId, selectPackage, isNavigating } = usePackageSelection();
    const resolvedValues = useMemo(()=>{
        var _relationship_buyer_firstName, _relationship_buyer_lastName;
        return buildSystemVariableResolution({
            clientCompanyName: relationshipModel.customerName(relationship),
            clientFirstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : undefined,
            clientLastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : undefined,
            clientEmail: relationship.buyer.email,
            vendorBusinessName: relationship.vendor.name,
            agreementName: relationship.title,
            effectiveDate: relationship.effectiveDate ? toDate(relationship.effectiveDate) : null,
            currentDate: getNow()
        });
    }, [
        relationship,
        getNow
    ]);
    const canSelect = isPreview || isBuyer || !hasAdditionalRecipients;
    const showOnlyPrimaryCallout = !canSelect;
    const containerRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(undefined);
    useEffect(()=>{
        if (isMobile || !containerRef.current) return;
        const calculateHeight = ()=>{
            const container = containerRef.current;
            if (!container) return;
            const cards = container.querySelectorAll('[data-bundleid]');
            if (cards.length === 0) return;
            setCardHeight(undefined);
            requestAnimationFrame(()=>{
                let maxContentHeight = 0;
                cards.forEach((card)=>{
                    const cardElement = card;
                    maxContentHeight = Math.max(maxContentHeight, cardElement.scrollHeight);
                });
                if (maxContentHeight > 0) {
                    setCardHeight(maxContentHeight);
                }
            });
        };
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return ()=>window.removeEventListener('resize', calculateHeight);
    }, [
        isMobile,
        packages
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        className: styles.root,
        testId: SelectAPackageTestIds.Root(),
        gap: 24,
        children: [
            showOnlyPrimaryCallout && /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                className: styles.callout,
                testId: SelectAPackageTestIds.OnlyBuyerMessage(),
                children: [
                    /*#__PURE__*/ _jsx(Bulb, {
                        className: styles.primaryIcon
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "paragraph1",
                        children: [
                            "Only ",
                            buyerFullName,
                            " can select a package for this proposal"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Row, {
                ref: containerRef,
                className: isMobile ? styles.packagesListMobile : styles.packagesList,
                testId: SelectAPackageTestIds.PackagesList(),
                children: packages.map((pkg, idx)=>/*#__PURE__*/ _jsx(Box, {
                        className: isMobile ? undefined : styles.packageCardWrapper,
                        testId: SelectAPackageTestIds.PackageCardWrapper(idx),
                        children: /*#__PURE__*/ _jsx(PackageCard, {
                            package: pkg,
                            isSelected: pkg.id === selectedBundleId,
                            isEffectiveDateInFuture: isEffectiveDateInFuture,
                            canSelect: canSelect,
                            onSelect: ()=>selectPackage(pkg.id),
                            resolvedValues: resolvedValues,
                            testId: "package-card-".concat(idx),
                            disabled: isNavigating,
                            style: cardHeight ? {
                                height: cardHeight
                            } : undefined
                        })
                    }, pkg.id))
            })
        ]
    });
};
